// Demo data for the Demografi screen under ?demo=1 and in tests. Invented
// people, real region names, deterministic (seeded) so every render and audit
// sees the same numbers. Built per person, then aggregated exactly as the
// server does, so the screen sees a consistent shape.
import type { DataPage, Demografi, DemografiScope, NikCheck, SubmissionDetail } from "./types";

type Tree = Record<string, { nama: string; kab: Record<string, { nama: string; kec: Record<string, string> }> }>;

const TREE: Tree = {
  "32": {
    nama: "Jawa Barat",
    kab: {
      "3201": { nama: "Kabupaten Bogor", kec: { "320101": "Cibinong", "320102": "Gunung Putri", "320103": "Citeureup" } },
      "3216": { nama: "Kabupaten Bekasi", kec: { "321601": "Tarumajaya", "321602": "Babelan", "321603": "Sukawangi" } },
      "3276": { nama: "Kota Depok", kec: { "327601": "Pancoran Mas", "327602": "Cimanggis", "327603": "Sawangan" } },
    },
  },
  "31": {
    nama: "Daerah Khusus Ibukota Jakarta",
    kab: {
      "3171": { nama: "Kota Administrasi Jakarta Pusat", kec: { "317101": "Gambir", "317102": "Sawah Besar" } },
      "3174": { nama: "Kota Administrasi Jakarta Selatan", kec: { "317401": "Tebet", "317402": "Setiabudi" } },
    },
  },
  "36": {
    nama: "Banten",
    kab: {
      "3674": { nama: "Kota Tangerang Selatan", kec: { "367401": "Serpong", "367402": "Serpong Utara" } },
      "3603": { nama: "Kabupaten Tangerang", kec: { "360301": "Balaraja", "360302": "Jayanti" } },
    },
  },
  "18": {
    nama: "Lampung",
    kab: { "1801": { nama: "Kabupaten Lampung Selatan", kec: { "180101": "Natar", "180102": "Jati Agung" } } },
  },
  "33": { nama: "Jawa Tengah", kab: { "3310": { nama: "Kabupaten Klaten", kec: { "331001": "Prambanan" } } } },
  "73": { nama: "Sulawesi Selatan", kab: { "7371": { nama: "Kota Makassar", kec: { "737101": "Mariso" } } } },
  "91": { nama: "Papua", kab: { "9171": { nama: "Kota Jayapura", kec: { "917101": "Jayapura Utara" } } } },
};

// Relative weight per province (demo only).
const WEIGHT: Record<string, number> = { "32": 46, "31": 18, "36": 16, "18": 9, "33": 6, "73": 3, "91": 2 };

interface Person {
  id: string;
  name: string;
  nik: string;
  minutesAgo: number;
  prov: string;
  kab: string;
  kec: string;
  y: number;
  m: number;
  d: number;
  g: "L" | "P";
}

function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0x1_0000_0000;
  };
}

function pick<T>(r: () => number, items: T[]): T {
  return items[Math.floor(r() * items.length)]!;
}

const FIRST = ["Siti", "Budi", "Ayu", "Rizki", "Dewi", "Agus", "Nur", "Fajar", "Putri", "Hendra", "Rina", "Yoga", "Lestari", "Dimas", "Wulan", "Bayu"];
const LAST = ["Rahmawati", "Santoso", "Pratama", "Hidayat", "Saputra", "Lestari", "Wibowo", "Kurniawan", "Maharani", "Nugroho", "Puspita", "Setiawan"];

let PEOPLE: Person[] | null = null;
function people(): Person[] {
  if (PEOPLE) return PEOPLE;
  const r = rng(20261015);
  const provs = Object.entries(WEIGHT).flatMap(([code, w]) => Array.from({ length: w }, () => code));
  const out: Person[] = [];
  for (let i = 0; i < 1286; i += 1) {
    const prov = pick(r, provs);
    const kab = pick(r, Object.keys(TREE[prov]!.kab));
    const kec = pick(r, Object.keys(TREE[prov]!.kab[kab]!.kec));
    // Volunteers skew 18 to 45.
    const age = Math.min(80, Math.max(15, Math.round(30 + (r() + r() + r() - 1.5) * 22)));
    const m = 1 + Math.floor(r() * 12);
    const d = 1 + Math.floor(r() * 28);
    const g: "L" | "P" = r() < 0.53 ? "L" : "P";
    const y = 2026 - age;
    const nik = `${kec}${String(g === "P" ? d + 40 : d).padStart(2, "0")}${String(m).padStart(2, "0")}${String(y % 100).padStart(2, "0")}${String(1 + Math.floor(r() * 40)).padStart(4, "0")}`;
    out.push({
      id: `00000000-0000-4000-8000-${String(i).padStart(12, "0")}`,
      name: `${pick(r, FIRST)} ${pick(r, LAST)}`,
      nik,
      minutesAgo: i * 37,
      prov,
      kab,
      kec,
      y,
      m,
      d,
      g,
    });
  }
  PEOPLE = out;
  return out;
}

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const HARI_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const ZODIAK: [string, number, number][] = [
  ["Capricorn", 1, 1],
  ["Aquarius", 1, 20],
  ["Pisces", 2, 19],
  ["Aries", 3, 21],
  ["Taurus", 4, 20],
  ["Gemini", 5, 21],
  ["Cancer", 6, 21],
  ["Leo", 7, 23],
  ["Virgo", 8, 23],
  ["Libra", 9, 23],
  ["Scorpio", 10, 23],
  ["Sagitarius", 11, 22],
  ["Capricorn", 12, 22],
];
const SHIO = ["Tikus", "Kerbau", "Macan", "Kelinci", "Naga", "Ular", "Kuda", "Kambing", "Monyet", "Ayam", "Anjing", "Babi"];
const ZONA: Record<string, string> = { "32": "WIB", "31": "WIB", "36": "WIB", "18": "WIB", "33": "WIB", "73": "WITA", "91": "WIT" };

function gen(y: number): string {
  if (y >= 2013) return "Gen Alpha";
  if (y >= 1997) return "Gen Z";
  if (y >= 1981) return "Millennial";
  if (y >= 1965) return "Gen X";
  if (y >= 1946) return "Baby Boomer";
  return "Pra-Boomer";
}

function tally(labels: string[], values: string[]) {
  const m = new Map<string, number>();
  for (const v of values) m.set(v, (m.get(v) ?? 0) + 1);
  return labels.map((label) => ({ label, total: m.get(label) ?? 0 }));
}

const GEN_YEARS: Record<string, [number, number]> = {
  "Gen Alpha": [2013, 2100],
  "Gen Z": [1997, 2012],
  Millennial: [1981, 1996],
  "Gen X": [1965, 1980],
  "Baby Boomer": [1946, 1964],
  "Pra-Boomer": [1800, 1945],
};

function ageOf(p: Person, now: Date): number {
  const t = { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() };
  return t.y - p.y - (t.m > p.m || (t.m === p.m && t.d >= p.d) ? 0 : 1);
}

/** The same filters the server applies (status aside: demo people are all valid). */
function matching(scope: DemografiScope, now: Date): Person[] {
  const q = scope.q?.toLowerCase();
  return people().filter((p) => {
    if (scope.prov && p.prov !== scope.prov) return false;
    if (scope.kab && p.kab !== scope.kab) return false;
    if (scope.kec && p.kec !== scope.kec) return false;
    if (scope.gender && p.g !== scope.gender) return false;
    const a = ageOf(p, now);
    if (scope.umurMin !== undefined && a < scope.umurMin) return false;
    if (scope.umurMax !== undefined && a > scope.umurMax) return false;
    if (scope.generasi) {
      const [lo, hi] = GEN_YEARS[scope.generasi]!;
      if (p.y < lo || p.y > hi) return false;
    }
    if (scope.zona && ZONA[p.prov] !== scope.zona) return false;
    if (scope.status && scope.status !== "valid" && scope.status !== "semua") return false;
    if (q && !(/^\d+$/.test(q) ? p.nik.startsWith(q) : p.name.toLowerCase().includes(q))) return false;
    return true;
  });
}

export function fixtureDemografi(scope: DemografiScope, now: Date): Demografi {
  const all = matching(scope, now);
  const age = (p: Person) => ageOf(p, now);
  const ages = all.map(age).sort((a, b) => a - b);
  const level = scope.kab ? "kec" : scope.prov ? "kab" : "prov";
  const regionOf = (p: Person) => (level === "kec" ? p.kec : level === "kab" ? p.kab : p.prov);
  const nameOf = (code: string) =>
    level === "prov"
      ? TREE[code]!.nama
      : level === "kab"
        ? TREE[scope.prov!]!.kab[code]!.nama
        : TREE[scope.prov!]!.kab[scope.kab!]!.kec[code]!;
  const regions = new Map<string, { l: number; p: number }>();
  for (const p of all) {
    const r = regions.get(regionOf(p)) ?? { l: 0, p: 0 };
    if (p.g === "L") r.l += 1;
    else r.p += 1;
    regions.set(regionOf(p), r);
  }
  const bands: [string, number, number][] = [
    ["Di bawah 17", 0, 16],
    ["17-24", 17, 24],
    ["25-34", 25, 34],
    ["35-44", 35, 44],
    ["45-54", 45, 54],
    ["55-64", 55, 64],
    ["65+", 65, 999],
  ];
  const decades = [...new Set(all.map((p) => `${Math.floor(p.y / 10) * 10}-an`))].sort();
  const n = all.length;
  const shioOf = (p: Person) => SHIO[(((p.y - (p.m < 2 || (p.m === 2 && p.d < 5) ? 1 : 0) - 4) % 12) + 12) % 12]!;
  const zod = (p: Person) => {
    let s = "Capricorn";
    for (const [name, m, d] of ZODIAK) if (p.m > m || (p.m === m && p.d >= d)) s = name;
    return s;
  };
  const voters = ages.filter((a) => a >= 17).length;
  const review = Math.round(n * 0.031);
  return {
    generatedAt: now.toISOString(),
    scope: {
      level: scope.kec ? "kecamatan" : scope.kab ? "kabkota" : scope.prov ? "provinsi" : "nasional",
      prov: scope.prov ? { code: scope.prov, nama: TREE[scope.prov]?.nama ?? scope.prov } : null,
      kab: scope.kab && scope.prov ? { code: scope.kab, nama: TREE[scope.prov]?.kab[scope.kab]?.nama ?? scope.kab } : null,
      kec:
        scope.kec && scope.kab && scope.prov
          ? { code: scope.kec, nama: TREE[scope.prov]?.kab[scope.kab]?.kec[scope.kec] ?? scope.kec }
          : null,
    },
    difilter: Boolean(scope.gender || scope.status || scope.umurMin !== undefined || scope.umurMax !== undefined || scope.generasi || scope.zona || scope.q),
    totals: { orang: n, laki: all.filter((p) => p.g === "L").length, perempuan: all.filter((p) => p.g === "P").length },
    validasi: {
      valid: n - review,
      review,
      rejected: Math.round(n * 0.004),
      invalid: 0,
      jevVerified: Math.round(n * 0.03),
      jevUnverified: Math.min(review, 3),
      ditolakBot: 57,
    },
    wilayah: {
      level,
      items: [...regions.entries()]
        .map(([code, v]) => ({ code, nama: nameOf(code), dikenal: true, total: v.l + v.p, l: v.l, p: v.p }))
        .sort((a, b) => b.total - a.total || a.code.localeCompare(b.code)),
    },
    zonaWaktu: tally(
      ["WIB", "WITA", "WIT"],
      all.map((p) => ZONA[p.prov]!),
    ),
    usia: {
      rataRata: n ? Math.round((ages.reduce((s, a) => s + a, 0) / n) * 10) / 10 : null,
      median: n ? ages[Math.floor((n - 1) / 2)]! : null,
      kelompok: tally(
        ["Anak", "Dewasa", "Lansia"],
        ages.map((a) => (a < 18 ? "Anak" : a < 60 ? "Dewasa" : "Lansia")),
      ),
      rentang: tally(
        bands.map(([l]) => l),
        ages.map((a) => bands.find(([, lo, hi]) => a >= lo && a <= hi)![0]),
      ),
      piramida: bands.map(([label, lo, hi]) => {
        const inBand = all.filter((p) => age(p) >= lo && age(p) <= hi);
        return { label, l: inBand.filter((p) => p.g === "L").length, p: inBand.filter((p) => p.g === "P").length };
      }),
    },
    generasi: tally(
      ["Gen Alpha", "Gen Z", "Millennial", "Gen X", "Baby Boomer", "Pra-Boomer"],
      all.map((p) => gen(p.y)),
    ),
    dekadeLahir: tally(
      decades,
      all.map((p) => `${Math.floor(p.y / 10) * 10}-an`),
    ),
    bulanLahir: tally(
      BULAN,
      all.map((p) => BULAN[p.m - 1]!),
    ),
    hariLahir: tally(
      ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"],
      all.map((p) => HARI_ID[new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay()]!),
    ),
    zodiak: tally(
      ZODIAK.slice(1).map(([z]) => z),
      all.map(zod),
    ),
    shio: { items: tally(SHIO, all.map(shioOf)), perkiraan: all.filter((p) => p.m === 1 || (p.m === 2 && p.d <= 20)).length },
    hukum: {
      hakPilih: voters,
      belumHakPilih: n - voters,
      pemilihPemula: ages.filter((a) => a >= 17 && a <= 21).length,
      wajibKtp: voters,
      sim: [
        { label: "A / C / D", total: voters },
        { label: "B I", total: ages.filter((a) => a >= 20).length },
        { label: "B II", total: ages.filter((a) => a >= 21).length },
      ],
      pensiunJp5Tahun: ages.filter((a) => a >= 54 && a < 59).length,
      lewatUsiaPensiunJp: ages.filter((a) => a >= 59).length,
    },
  };
}

function placeOf(p: Person) {
  const prov = TREE[p.prov]!;
  const kab = prov.kab[p.kab]!;
  return { provinsi: prov.nama, kabkota: kab.nama, kecamatan: kab.kec[p.kec]! };
}

export function fixtureListData(scope: DemografiScope, page: number, pageSize: number, now: Date): DataPage {
  const rows = matching(scope, now);
  const slice = rows.slice((page - 1) * pageSize, page * pageSize);
  return {
    page,
    pageSize,
    total: rows.length,
    items: slice.map((p) => ({
      submissionId: p.id,
      nama: p.name,
      nik: p.nik,
      nomor: `+62812${p.nik.slice(-7)}`,
      pengirim: "Posko Kelurahan Cibinong",
      diterima: new Date(now.getTime() - p.minutesAgo * 60_000).toISOString(),
      jumlahKiriman: 1,
      ...placeOf(p),
      jenisKelamin: p.g === "P" ? "Perempuan" : "Laki-laki",
      tanggalLahir: `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`,
      umur: ageOf(p, now),
      generasi: gen(p.y),
      status: "valid",
      flags: [],
      jev: { choice: null, confidence: null },
    })),
  };
}

/** Demo detail: a demo person when the id is one, else the canned Prambanan answer. */
export function fixtureDetail(id: string, now: Date): SubmissionDetail | null {
  const p = people().find((x) => x.id === id);
  if (!p) return null;
  const base = fixtureCheck(p.nik);
  const place = placeOf(p);
  const age = ageOf(p, now);
  const ekstraksi: NikCheck = {
    ...base,
    decoded: {
      kode: { provinsi: p.prov, kabkota: p.kab, kecamatan: p.kec, tanggal: p.nik.slice(6, 12), serial: p.nik.slice(12) },
      ...place,
      jenisKelamin: p.g === "P" ? "Perempuan" : "Laki-laki",
      tanggalLahir: { hari: p.d, bulan: p.m, tahun: p.y, valid: true },
      serial: Number(p.nik.slice(12)),
    },
    turunan: base.turunan
      ? {
          ...base.turunan,
          tanggalLahir: `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`,
          umur: age,
          kelompokUsia: age < 18 ? "anak" : age < 60 ? "dewasa" : "lansia",
          generasi: gen(p.y) as never,
          hariLahir: HARI_ID[new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay()]!,
          zonaWaktu: ZONA[p.prov] as never,
          hukum: { ...base.turunan.hukum, hakPilih: age >= 17, wajibKtp: age >= 17, pemilihPemula: age >= 17 && age <= 21 },
        }
      : null,
  };
  return {
    kiriman: {
      id: p.id,
      nama: p.name,
      nik: p.nik,
      nomor: `+62812${p.nik.slice(-7)}`,
      pengirim: "Posko Kelurahan Cibinong",
      diterima: new Date(now.getTime() - p.minutesAgo * 60_000).toISOString(),
    },
    ekstraksi,
    terprofil: true,
    dinilaiPada: null,
  };
}

/** One canned answer for any NIK in the demo: a 1995 woman from Prambanan. */
export function fixtureCheck(nik: string): NikCheck {
  const digits = nik.replace(/[\s.-]/g, "");
  if (!/^\d{16}$/.test(digits)) {
    return {
      valid: false,
      status: "invalid",
      hard: [{ code: "length", reason: "NIK harus tepat 16 digit angka." }],
      soft: [],
      decoded: null,
      turunan: null,
      jev: null,
    };
  }
  return {
    valid: true,
    status: "valid",
    hard: [],
    soft: [],
    decoded: {
      kode: { provinsi: "33", kabkota: "3310", kecamatan: "331001", tanggal: "520895", serial: "0001" },
      provinsi: "Jawa Tengah",
      kabkota: "Kabupaten Klaten",
      kecamatan: "Prambanan",
      jenisKelamin: "Perempuan",
      tanggalLahir: { hari: 12, bulan: 8, tahun: 1995, valid: true },
      serial: 1,
    },
    turunan: {
      tanggalLahir: "1995-08-12",
      umur: 31,
      kelompokUsia: "dewasa",
      generasi: "Millennial",
      hariLahir: "Sabtu",
      zodiak: "Leo",
      shio: "Babi",
      shioPasti: true,
      zonaWaktu: "WIB",
      hukum: {
        hakPilih: true,
        pemilihPemula: false,
        wajibKtp: true,
        sim: [
          { golongan: "A", usiaMin: 17, layak: true, layakTahun: 2012 },
          { golongan: "C", usiaMin: 17, layak: true, layakTahun: 2012 },
          { golongan: "D", usiaMin: 17, layak: true, layakTahun: 2012 },
          { golongan: "B I", usiaMin: 20, layak: true, layakTahun: 2015 },
          { golongan: "B II", usiaMin: 21, layak: true, layakTahun: 2016 },
        ],
        pensiunJp: { tahun: 2060, usia: 65, sisaTahun: 34 },
        pensiunAsn: { bup58: 2053, bup60: 2055 },
      },
    },
    jev: null,
  };
}
